import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MyTrucksTableV2 } from "@/components/my-trucks/MyTrucksTableV2";
import { CarrierOffersSection } from "@/components/search/CarrierOffersSection";
import { DemoHint } from "@/components/demo/DemoHint";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";

type MyTrucksTab = "trucks" | "offers";

export default function MyTrucksPageV2() {
  usePageMeta({ title: "My Trucks | LaneRunner", description: "Manage your posted trucks" });

  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isCarrier =
    profile?.user_type === "carrier" || profile?.user_type === "broker_carrier";

  const initialTab: MyTrucksTab =
    searchParams.get("tab") === "offers" && isCarrier ? "offers" : "trucks";
  const [activeTab, setActiveTab] = useState<MyTrucksTab>(initialTab);

  // Sync from URL changes
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "offers" && isCarrier) setActiveTab("offers");
    else if (!tab || tab === "trucks") setActiveTab("trucks");
  }, [searchParams, isCarrier]);

  const handleTabChange = (tab: MyTrucksTab) => {
    setActiveTab(tab);
    if (tab === "offers") {
      setSearchParams({ tab: "offers" });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between">
        <div className="flex items-center gap-4">
          <DemoHint hintKey="page.myTrucks" side="bottom" showIndicator>
            <h1 className="text-xl font-bold text-foreground">My Trucks</h1>
          </DemoHint>
          {isCarrier && (
            <div className="flex items-center gap-0 border border-border rounded-md overflow-hidden">
              <button
                onClick={() => handleTabChange("trucks")}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTab === "trucks"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-muted-foreground hover:bg-muted"
                }`}
              >
                MY TRUCKS
              </button>
              <button
                onClick={() => handleTabChange("offers")}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTab === "offers"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-muted-foreground hover:bg-muted"
                }`}
              >
                MY OFFERS
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 p-4 min-h-0">
        {activeTab === "offers" && isCarrier ? (
          <div className="h-full overflow-y-auto">
            <CarrierOffersSection />
          </div>
        ) : (
          <MyTrucksTableV2 />
        )}
      </div>
    </div>
  );
}
